// Original items written for this site, inspired by the public RIASEC
// (Holland Code) career-interest framework: Realistic, Investigative,
// Artistic, Social, Enterprising, Conventional. An informal self-reflection
// quiz, not a validated career assessment.

export type RiasecCategory = 'R' | 'I' | 'A' | 'S' | 'E' | 'C';

export interface RiasecQuestion {
  id: string;
  text: string;
  category: RiasecCategory;
}

export const RIASEC_CATEGORIES: RiasecCategory[] = ['R', 'I', 'A', 'S', 'E', 'C'];

export const RIASEC_LABELS: Record<RiasecCategory, { name: string; blurb: string }> = {
  R: {
    name: 'Realistic (Doer)',
    blurb: 'Drawn to hands-on, practical work with tools, machines, or the outdoors.',
  },
  I: {
    name: 'Investigative (Thinker)',
    blurb: 'Drawn to analysis, research, and figuring out how things really work.',
  },
  A: {
    name: 'Artistic (Creator)',
    blurb: 'Drawn to original, expressive work with real creative freedom.',
  },
  S: {
    name: 'Social (Helper)',
    blurb: 'Drawn to teaching, caring for, or directly supporting other people.',
  },
  E: {
    name: 'Enterprising (Persuader)',
    blurb: 'Drawn to leading, selling, negotiating, and taking calculated risks.',
  },
  C: {
    name: 'Conventional (Organizer)',
    blurb: 'Drawn to structure, accuracy, and clear, repeatable processes.',
  },
};

export const RIASEC_QUESTIONS: RiasecQuestion[] = [
  // Realistic
  {
    id: 'r1',
    text: "I'd rather build or fix something with my hands than write a report about it.",
    category: 'R',
  },
  { id: 'r2', text: 'I enjoy working with tools, machines, or physical materials.', category: 'R' },
  {
    id: 'r3',
    text: 'I prefer being outdoors or in a workshop over sitting at a desk all day.',
    category: 'R',
  },
  { id: 'r4', text: 'I like seeing a concrete, physical result from my work.', category: 'R' },
  {
    id: 'r5',
    text: "I'm drawn to jobs that involve using my hands or operating equipment.",
    category: 'R',
  },
  // Investigative
  {
    id: 'i1',
    text: 'I enjoy digging into a problem until I understand exactly why it happens.',
    category: 'I',
  },
  {
    id: 'i2',
    text: "I'd rather research a topic deeply than take someone else's word for it.",
    category: 'I',
  },
  {
    id: 'i3',
    text: 'Solving a hard puzzle or technical problem is genuinely satisfying to me.',
    category: 'I',
  },
  { id: 'i4', text: "I like asking 'why' more than most people around me.", category: 'I' },
  {
    id: 'i5',
    text: "I'm drawn to work that involves data, science, or analysis.",
    category: 'I',
  },
  // Artistic
  {
    id: 'a1',
    text: 'I need creative freedom in my work, not a fixed template to follow.',
    category: 'A',
  },
  {
    id: 'a2',
    text: 'I express myself better through writing, design, music, or art than through spreadsheets.',
    category: 'A',
  },
  {
    id: 'a3',
    text: "Following someone else's rigid process kills my motivation.",
    category: 'A',
  },
  {
    id: 'a4',
    text: 'I notice and care about aesthetics, how something looks or sounds.',
    category: 'A',
  },
  {
    id: 'a5',
    text: "I'm drawn to work that lets me create something original.",
    category: 'A',
  },
  // Social
  {
    id: 's1',
    text: 'Helping someone solve a personal problem feels more rewarding than closing a deal.',
    category: 'S',
  },
  {
    id: 's2',
    text: 'I naturally end up as the person others come to for advice or support.',
    category: 'S',
  },
  {
    id: 's3',
    text: "I'd rather teach or mentor someone than work alone on a task.",
    category: 'S',
  },
  {
    id: 's4',
    text: 'I pay close attention to how the people around me are feeling.',
    category: 'S',
  },
  {
    id: 's5',
    text: "I'm drawn to work that directly helps or cares for other people.",
    category: 'S',
  },
  // Enterprising
  {
    id: 'e1',
    text: "I enjoy convincing people, whether it's an idea, a plan, or a sale.",
    category: 'E',
  },
  {
    id: 'e2',
    text: "I like being in charge of a project rather than just executing someone else's plan.",
    category: 'E',
  },
  { id: 'e3', text: "Taking a risk for a bigger reward doesn't scare me much.", category: 'E' },
  {
    id: 'e4',
    text: 'I naturally think about how to grow or scale something, not just maintain it.',
    category: 'E',
  },
  {
    id: 'e5',
    text: "I'm drawn to work that involves leading, selling, or negotiating.",
    category: 'E',
  },
  // Conventional
  {
    id: 'c1',
    text: 'I like having a clear process, checklist, or system to follow.',
    category: 'C',
  },
  {
    id: 'c2',
    text: 'Keeping things organized, accurate, and on schedule satisfies me.',
    category: 'C',
  },
  {
    id: 'c3',
    text: "I'd rather double-check the details than move fast and fix it later.",
    category: 'C',
  },
  { id: 'c4', text: 'Clear rules and structure make me more productive, not less.', category: 'C' },
  {
    id: 'c5',
    text: "I'm drawn to work that involves organizing, record-keeping, or data entry.",
    category: 'C',
  },
];
